import React, { FC } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@RHCommerceDev/utils/cn";
import { Slot } from "@radix-ui/react-slot";

export const typographyVariants = cva("", {
  variants: {
    variant: {
      p: "text-[11px] tracking-[0.165px]",
      h1: "text-xl uppercase",
      caption: "inline text-[11px]",
      error: "font-primary-thin font-light text-xs text-rh-red",
      body1: "text-[15px] leading-[1.65]",
      body2: "text-[13px] leading-[1.56] font-thin",
      body3: "text-[12px] leading-[1.44]",
      body4: "text-[10px] leading-[1.2] tracking-[0.02em]",
      body5: "font-primary-rhlight text-[10px] leading-[120%] tracking-normal",
      button1: "font-light text-[11px] leading-[1.32]",
      default: ""
    },
    fontFamily: {
      rhSans: "font-rhsans",
      rhSansRoman: "font-rhsans-roman",
      rhThin: "font-primary-rhthin",
      rhcRoman: "font-rhc-roman",
      rhc: "font-rhc",
      rhcHairline: "font-rhc-hairline"
    },
    fontWeight: {
      thin: "font-thin",
      extraLight: "font-extralight",
      light: "font-light",
      normal: "font-normal",
      semibold: "font-semibold"
    }
  },
  defaultVariants: {
    variant: "p",
    fontFamily: "rhSans",
    fontWeight: "thin"
  },
  compoundVariants: [
    {
      variant: ["p", "h1", "caption", "error"],
      className:
        "inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none"
    }
  ]
});

interface TypographyProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean;
}

export const Typography: FC<TypographyProps> = ({
  children,
  className,
  variant,
  fontFamily,
  fontWeight,
  asChild,
  ...props
}) => {
  const Comp = asChild ? Slot : "p";

  return (
    <Comp
      className={cn(
        typographyVariants({ variant, fontFamily, fontWeight }),
        className
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};
